// Schritt 1 des Serienmail-Assistenten — Empfaenger: Zielgruppen frei kombinierbar
// (Mieter/Eigentuemer/Dienstleister/Sonstige, #577), Bereich bestimmte Objekte oder global,
// Mehrfachauswahl der Objekte, optional Einheiten (nur Mieter). Zaehler und Trefferliste kommen
// AUSSCHLIESSLICH aus teileAuf (im Elternteil gerechnet) — hier wird nichts eigenes
// aufsummiert, damit die Zahlen nicht auseinanderlaufen (siehe Spec).
import React, { useMemo, useState } from 'react';
import {
  AddressBook, Buildings, CaretDown, CaretRight, CheckSquare, CircleNotch, Crown, Door, DownloadSimple, EnvelopeOpen, EnvelopeSimple, Globe, Printer, Square, Users, Warning, Wrench, X,
  type Icon,
} from '@phosphor-icons/react';
import { ImmoButton, Mehrfachauswahl } from '../../../components/immobilien/ui';
import { useMobilModus } from '../../../lib/immobilien/mobil';
import { LABEL, Umschalter } from '../emails/bereich/Formular';
import { baueCsv } from '../../../lib/serienmail/druckliste';
import { filtereObjekte } from '../../../lib/serienmail/objektSuche';
import { ZIELGRUPPEN, type Kandidat, type Zielgruppe } from '../../../lib/serienmail/typen';
import type { Objekt, Einheit, SetZustand, Zustand } from './SerienmailAssistent';

interface SchrittEmpfaengerProps {
  zustand: Zustand;
  setZustand: SetZustand;
  objekte: Objekt[];
  einheiten: Einheit[];
  kandidatenLoading: boolean;
  kandidatenFehler: string | null;
  /** #579: Kontakte, die nicht aktiv sind und deshalb nicht in der Liste stehen. */
  ausgeblendet: number;
  erreichbar: Kandidat[];
  ohneAdresse: Kandidat[];
  /** Abschluss-Review I10: Zahl der wegen gleicher Adresse uebersprungenen Personen. */
  doppelt: number;
  onDruckansichtOeffnen: () => void;
}

/** CSV mit BOM + charset=utf-8 herunterladen — ohne BOM zerlegt Excel die Umlaute. */
function ladeCsvHerunter(ohneAdresse: Kandidat[]) {
  const csv = baueCsv(ohneAdresse);
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `serienmail-druckliste-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Muss exakt zum Platzhaltertext in lib/serienmail/empfaenger.ts passen (Fallback,
 * wenn Vor-/Nachname bzw. Firma leer sind) — sonst zaehlt der Hinweis unten daneben. */
const OHNE_NAMEN = 'Ohne Namen';

// #577: Zielgruppen frei kombinierbar; Bezeichnung auch in der Trefferliste.
const GRUPPE: Record<Zielgruppe, { label: string; icon: Icon }> = {
  mieter: { label: 'Mieter', icon: Users },
  eigentuemer: { label: 'Eigentümer', icon: Crown },
  dienstleister: { label: 'Dienstleister', icon: Wrench },
  kontakt: { label: 'Sonstige', icon: AddressBook },
};

const SchrittEmpfaenger: React.FC<SchrittEmpfaengerProps> = ({
  zustand, setZustand, objekte, einheiten, kandidatenLoading, kandidatenFehler, ausgeblendet, erreichbar, ohneAdresse, doppelt, onDruckansichtOeffnen,
}) => {
  const [ohneAdresseOffen, setOhneAdresseOffen] = useState(false);
  const { mobil } = useMobilModus();
  // Die Suche in Objekten/Einheiten sitzt im Auswahl-Fenster (Mehrfachauswahl); die Regel bleibt
  // filtereObjekte aus lib/serienmail/objektSuche (gewählte bleiben sichtbar, Umlaute normalisiert).

  // Nur unter den AUSGEWAEHLTEN zaehlen (nicht allen Erreichbaren) — abgewaehlte
  // Empfaenger bekommen ohnehin keine Mail, ihr fehlender Name ist hier irrelevant.
  const ohneNamenAusgewaehlt = useMemo(
    () => erreichbar.filter((k) => !zustand.abgewaehlt.has(k.owner_id) && k.name === OHNE_NAMEN).length,
    [erreichbar, zustand.abgewaehlt],
  );

  const toggleObjekt = (id: string) => {
    setZustand((z) => ({
      ...z,
      objektIds: z.objektIds.includes(id) ? z.objektIds.filter((o) => o !== id) : [...z.objektIds, id],
      // Ein Objektwechsel macht die bisherige Einheitenauswahl gegenstandslos.
      einheitIds: [],
    }));
  };

  const toggleGruppe = (g: Zielgruppe) => {
    setZustand((z) => ({
      ...z,
      zielgruppen: z.zielgruppen.includes(g) ? z.zielgruppen.filter((x) => x !== g) : ZIELGRUPPEN.filter((x) => x === g || z.zielgruppen.includes(x)),
    }));
  };
  const global = zustand.bereich === 'alle';
  // Liste/Bilanz erst, wenn eine Gruppe gewählt ist und (global oder) Objekte gewählt sind.
  const auswahlVollstaendig = zustand.zielgruppen.length > 0 && (global || zustand.objektIds.length > 0);

  const toggleEinheit = (id: string) => {
    setZustand((z) => ({ ...z, einheitIds: z.einheitIds.includes(id) ? z.einheitIds.filter((e) => e !== id) : [...z.einheitIds, id] }));
  };

  const toggleErreichbar = (owner_id: string) => {
    setZustand((z) => {
      const abgewaehlt = new Set(z.abgewaehlt);
      if (abgewaehlt.has(owner_id)) abgewaehlt.delete(owner_id); else abgewaehlt.add(owner_id);
      return { ...z, abgewaehlt };
    });
  };

  const anzahlAusgewaehlt = erreichbar.length - zustand.abgewaehlt.size;
  // „Alle auswählen“ (Entscheidung 12): alle Erreichbaren an bzw. – wenn schon alle an sind – alle ab.
  const alleGewaehlt = erreichbar.length > 0 && zustand.abgewaehlt.size === 0;
  const alleUmschalten = () => setZustand((z) => ({
    ...z,
    abgewaehlt: alleGewaehlt ? new Set(erreichbar.map((k) => k.owner_id)) : new Set(),
  }));
  const objektName = (id: string) => objekte.find((o) => o.id === id)?.bezeichnung ?? '…';
  const kartenStil: React.CSSProperties = { borderRadius: 24, padding: mobil ? 14 : 18, gap: 14 };

  return (
    <div className="flex flex-col min-w-0" style={{ gap: 12 }}>
      {/* Zielgruppe + Objekte + Einheiten */}
      <section className="immo-glas flex flex-col min-w-0" style={kartenStil}>
        <div className="flex flex-col" style={{ gap: 6 }}>
          <span style={LABEL}>Zielgruppen <span style={{ fontWeight: 500 }}>(mehrere möglich)</span></span>
          <div className="flex flex-wrap" style={{ gap: 6 }}>
            {ZIELGRUPPEN.map((g) => {
              const an = zustand.zielgruppen.includes(g);
              const I = GRUPPE[g].icon;
              return (
                <button key={g} type="button" aria-pressed={an} onClick={() => toggleGruppe(g)} className="flex items-center cursor-pointer"
                  style={{ gap: 6, height: mobil ? 44 : 34, padding: '0 14px', borderRadius: 999, border: 'none', fontSize: 13, fontWeight: 700,
                    background: an ? 'var(--acc-fill)' : 'var(--fill2)', color: an ? 'var(--acc-ink)' : 'var(--ink2)' }}>
                  {an ? <CheckSquare size={15} weight="fill" /> : <I size={15} weight="bold" />}{GRUPPE[g].label}
                </button>
              );
            })}
          </div>
          {zustand.zielgruppen.length === 0 && <span style={{ fontSize: 12, color: 'var(--warn)', fontWeight: 700 }}>Bitte mindestens eine Zielgruppe wählen.</span>}
        </div>

        <div className="flex flex-col" style={{ gap: 6 }}>
          <span style={LABEL}>Bereich</span>
          <div className="self-start">
            <Umschalter hoehe={mobil ? 44 : 34} wert={zustand.bereich} onChange={(b) => setZustand((z) => ({ ...z, bereich: b }))}
              optionen={[{ key: 'objekte', label: 'Bestimmte Objekte', icon: <Buildings size={14} weight="bold" /> }, { key: 'alle', label: 'Alle (global)', icon: <Globe size={14} weight="bold" /> }]} />
          </div>
          {global && (
            <span style={{ fontSize: 12, color: 'var(--ink3)' }}>
              Alle aktiven Kontakte der gewählten Gruppen – Mieter und Eigentümer aus allen verwalteten Objekten (ohne archivierte), Dienstleister und sonstige Kontakte auch ohne Objektbezug.
            </span>
          )}
        </div>

        {!global && <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: 12 }}>
          <div className="flex flex-col min-w-0" style={{ gap: 6 }}>
            <span style={LABEL}>Objekte</span>
            {objekte.length === 0 ? (
              <span style={{ fontSize: 13, color: 'var(--ink3)', minHeight: 44, display: 'flex', alignItems: 'center' }}>Lade Objekte…</span>
            ) : (
              <Mehrfachauswahl
                icon={Buildings}
                titel="Objekte"
                knopfText={zustand.objektIds.length > 0 ? `${zustand.objektIds.length} gewählt` : 'Objekt suchen…'}
                platzhalter="Objekt suchen…"
                gewaehlt={zustand.objektIds}
                onUmschalten={toggleObjekt}
                filter={(suche) => filtereObjekte(objekte, suche, new Set(zustand.objektIds))}
                leerText={(suche) => `Kein Objekt passt zu „${suche.trim()}“.`}
                mobil={mobil}
              />
            )}
          </div>

          {/* Einheiten — nur für Mieter, wenn Objekte gewaehlt sind und welche existieren */}
          {zustand.zielgruppen.includes('mieter') && zustand.objektIds.length > 0 && einheiten.length > 0 && (
            <div className="flex flex-col min-w-0" style={{ gap: 6 }}>
              <span style={LABEL}>Einheiten der Mieter <span style={{ fontWeight: 500 }}>(leer = alle Einheiten der gewählten Objekte)</span></span>
              <Mehrfachauswahl
                icon={Door}
                titel="Einheiten"
                knopfText={zustand.einheitIds.length > 0 ? `${zustand.einheitIds.length} gewählt` : 'Einheit suchen…'}
                platzhalter="Einheit suchen…"
                gewaehlt={zustand.einheitIds}
                onUmschalten={toggleEinheit}
                filter={(suche) => filtereObjekte(einheiten, suche, new Set(zustand.einheitIds))}
                leerText={(suche) => `Keine Einheit passt zu „${suche.trim()}“.`}
                mobil={mobil}
              />
            </div>
          )}
        </div>}

        {!global && zustand.objektIds.length > 0 && (
          <div className="flex flex-col" style={{ gap: 8 }}>
            <div className="flex flex-wrap" style={{ gap: 6 }}>
              {zustand.objektIds.map((id) => (
                <span key={id} className="inline-flex items-center min-w-0" style={{ maxWidth: '100%', height: mobil ? 44 : 30, padding: mobil ? '0 0 0 12px' : '0 4px 0 12px', gap: 4, borderRadius: mobil ? 22 : 15, background: 'var(--fill2)', fontSize: 12, fontWeight: 700 }}>
                  <span className="truncate">{objektName(id)}</span>
                  <button type="button" aria-label={`${objektName(id)} entfernen`} onClick={() => toggleObjekt(id)} className="flex items-center justify-center cursor-pointer shrink-0"
                    style={{ width: mobil ? 44 : 24, height: mobil ? 44 : 24, borderRadius: '50%', border: 'none', background: 'transparent', color: 'var(--ink3)' }}>
                    <X size={11} weight="bold" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex">
              <ImmoButton groesse={mobil ? 44 : 36} onClick={() => setZustand((z) => ({ ...z, objektIds: [], einheitIds: [] }))}>Auswahl leeren</ImmoButton>
            </div>
          </div>
        )}
      </section>

      {kandidatenFehler && (
        <div role="alert" style={{ borderRadius: 14, padding: '10px 14px', background: 'var(--bad-soft)', color: 'var(--bad)', fontSize: 13, fontWeight: 700 }}>{kandidatenFehler}</div>
      )}

      {auswahlVollstaendig && (
        <>
          {/* Dauerhaft sichtbare Bilanz — 299 von 683 sind im echten Bestand ohne Adresse,
              die Zahl darf hier nicht klein oder versteckt sein. */}
          <section className="immo-glas flex flex-col min-w-0" style={{ borderRadius: 20, padding: '12px 16px', gap: 4 }}>
            {kandidatenLoading ? (
              <span className="flex items-center" style={{ gap: 8, fontSize: 13, fontWeight: 700, color: 'var(--ink3)' }}>
                <CircleNotch size={14} className="animate-spin" /> Lade Empfänger…
              </span>
            ) : (
              <>
                <span className="flex items-center flex-wrap" style={{ gap: '4px 10px', fontSize: 14, fontWeight: 800 }}>
                  <span className="flex items-center" style={{ gap: 6, color: 'var(--ok)' }}><EnvelopeSimple size={15} weight="fill" />{anzahlAusgewaehlt} von {erreichbar.length} erreichbar ausgewählt</span>
                  <span aria-hidden style={{ color: 'var(--ink3)' }}>·</span>
                  <span className="flex items-center" style={{ gap: 6, color: 'var(--warn)' }}><EnvelopeOpen size={15} weight="fill" />{ohneAdresse.length} ohne E-Mail-Adresse</span>
                </span>
                {/* Abschluss-Review I10: entdoppelte Personen tauchen in keiner der beiden
                    Zahlen auf. Ohne diesen Hinweis deckt die Bilanz nicht mehr alle aktiven
                    Mieter ab und die Differenz waere von hier aus nicht erklaerbar. */}
                {doppelt > 0 && (
                  <span style={{ fontSize: 12, color: 'var(--ink2)' }}>
                    {doppelt} {doppelt === 1 ? 'Person teilt sich' : 'Personen teilen sich'} eine Adresse mit jemand anderem und {doppelt === 1 ? 'wird' : 'werden'} einmal angeschrieben.
                  </span>
                )}
                {/* #579: inaktive Kontakte stehen bewusst nicht in der Liste – Zahl offenlegen. */}
                {ausgeblendet > 0 && (
                  <span style={{ fontSize: 12, color: 'var(--ink2)' }}>
                    {ausgeblendet} nicht {ausgeblendet === 1 ? 'aktiver Kontakt ist' : 'aktive Kontakte sind'} ausgeblendet (ehemalige Mieter, inaktive Eigentümer/Dienstleister/Kontakte, beendete Verträge, archivierte Objekte).
                  </span>
                )}
              </>
            )}
          </section>

          {/* Auffaellig, aber nicht blockierend: im Bestand haben mehrere aktive Mieter
              eine E-Mail-Adresse, aber keinen hinterlegten Namen. „Guten Tag {{empfaenger.name}}"
              wird bei ihnen dann woertlich zu „Guten Tag Ohne Namen" — der Nutzer soll das
              vorher sehen und selbst entscheiden (Anrede anpassen oder so verschicken). */}
          {!kandidatenLoading && ohneNamenAusgewaehlt > 0 && (
            <div className="flex items-start" style={{ gap: 8, borderRadius: 16, padding: '10px 14px', background: 'var(--warn-soft)', color: 'var(--warn)', fontSize: 13, fontWeight: 700 }}>
              <Warning size={16} weight="bold" className="shrink-0" style={{ marginTop: 1 }} />
              <span>
                {ohneNamenAusgewaehlt} {ohneNamenAusgewaehlt === 1 ? 'ausgewählte Person hat' : 'ausgewählte Personen haben'} keinen hinterlegten Namen —
                {' '}bei {ohneNamenAusgewaehlt === 1 ? 'ihr' : 'ihnen'} fällt eine Anrede mit {'{{empfaenger.name}}'} unpersönlich aus („Ohne Namen").
              </span>
            </div>
          )}

          {/* Trefferliste der Erreichbaren */}
          {erreichbar.length > 0 && (
            <section className="immo-glas flex flex-col min-w-0" style={{ borderRadius: 22, padding: 8 }}>
              <button type="button" onClick={alleUmschalten} aria-pressed={alleGewaehlt} className="flex items-center cursor-pointer text-left"
                style={{ gap: 10, minHeight: 44, padding: '0 8px', border: 'none', borderBottom: '1px solid var(--line)', background: 'transparent', color: 'var(--ink3)', fontSize: 12, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                {alleGewaehlt ? <CheckSquare size={19} weight="fill" color="var(--acc)" /> : <Square size={19} />}
                Alle auswählen
              </button>
              <div className="immo-scroll flex flex-col overflow-y-auto" style={{ maxHeight: 384 }}>
                {erreichbar.map((k) => {
                  const an = !zustand.abgewaehlt.has(k.owner_id);
                  return (
                    <label key={k.owner_id} className="immo-hover-flaeche flex items-center cursor-pointer min-w-0" style={{ gap: 10, minHeight: 52, padding: '6px 8px', borderRadius: 12 }}>
                      <input type="checkbox" checked={an} onChange={() => toggleErreichbar(k.owner_id)} className="sr-only" />
                      {an ? <CheckSquare size={19} weight="fill" color="var(--acc)" className="shrink-0" /> : <Square size={19} color="var(--ink3)" className="shrink-0" />}
                      <span className="flex flex-col flex-1 min-w-0">
                        <span className="truncate" style={{ fontSize: 14, fontWeight: 700 }}>{k.name}</span>
                        <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{[GRUPPE[k.owner_type].label, k.email, k.objekt_bezeichnung].filter(Boolean).join(' · ')}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </section>
          )}

          {!kandidatenLoading && erreichbar.length === 0 && ohneAdresse.length === 0 && (
            <span style={{ fontSize: 13, color: 'var(--ink3)', padding: '0 4px' }}>Keine aktiven Empfänger für diese Auswahl gefunden.</span>
          )}

          {/* Nicht-Erreichbare — aufklappbar mit Druckansicht + CSV fuer den Postweg */}
          {ohneAdresse.length > 0 && (
            <section className="flex flex-col min-w-0" style={{ borderRadius: 22, padding: 8, background: 'var(--warn-soft)' }}>
              <button type="button" onClick={() => setOhneAdresseOffen((v) => !v)} aria-expanded={ohneAdresseOffen} className="flex items-center justify-between cursor-pointer w-full"
                style={{ minHeight: 44, padding: '0 8px', border: 'none', background: 'transparent', color: 'var(--warn)', fontSize: 13, fontWeight: 800 }}>
                <span>{ohneAdresse.length} ohne E-Mail-Adresse anzeigen</span>
                {ohneAdresseOffen ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
              </button>
              {ohneAdresseOffen && (
                <div className="flex flex-col" style={{ gap: 10, padding: '0 4px 6px' }}>
                  <div className="immo-scroll flex flex-col overflow-y-auto" style={{ maxHeight: 256, borderRadius: 14, background: 'var(--bg)' }}>
                    {ohneAdresse.map((k) => {
                      const anschrift = [[k.strasse, k.hausnummer].filter(Boolean).join(' '), [k.plz, k.stadt].filter(Boolean).join(' ')].filter(Boolean).join(', ');
                      return (
                        <div key={k.owner_id} className="flex flex-col" style={{ padding: '9px 12px', borderBottom: '1px solid var(--line)' }}>
                          <span style={{ fontSize: 13, fontWeight: 700 }}>{k.name}</span>
                          <span style={{ fontSize: 12, color: anschrift ? 'var(--ink3)' : 'var(--bad)' }}>
                            {GRUPPE[k.owner_type].label} · {anschrift || 'Keine Anschrift hinterlegt'}{k.einheit_bezeichnung ? ` · ${k.einheit_bezeichnung}` : ''}{k.objekt_bezeichnung ? ` · ${k.objekt_bezeichnung}` : ''}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex flex-wrap" style={{ gap: 8 }}>
                    <ImmoButton groesse={mobil ? 44 : 36} icon={Printer} onClick={onDruckansichtOeffnen} style={{ background: 'var(--bg)' }}>Druckansicht</ImmoButton>
                    <ImmoButton groesse={mobil ? 44 : 36} icon={DownloadSimple} onClick={() => ladeCsvHerunter(ohneAdresse)} style={{ background: 'var(--bg)' }}>CSV herunterladen</ImmoButton>
                  </div>
                </div>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default SchrittEmpfaenger;
